import { supabase } from '../lib/supabase';

const AUTH_TIMEOUT_MS = 5000;

const isSchemaError = (error) => {
  if (!error) return false;
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  return false;
};

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

export const vehicleService = {
  async getCompanyId() {
    try {
      const getUserWithTimeout = Promise.race([
        supabase?.auth?.getUser(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('getUser timeout')), AUTH_TIMEOUT_MS)
        ),
      ]);
      const { data: { user } } = await getUserWithTimeout;
      if (!user) return null;
      const { data } = await supabase
        ?.from('user_profiles')
        ?.select('company_id')
        ?.eq('id', user?.id)
        ?.maybeSingle();
      return data?.company_id || null;
    } catch {
      return null;
    }
  },

  async create(vehicleData, explicitCompanyId) {
    try {
      // Use explicit companyId if provided (e.g. from selected client for portal users),
      // otherwise fall back to user_profiles lookup
      let companyId = explicitCompanyId || null;
      if (!companyId) {
        companyId = await this.getCompanyId();
      }

      // If VIN already exists, return existing vehicle instead of throwing
      if (vehicleData?.vin) {
        const existing = await this.getByVin(vehicleData?.vin);
        if (existing) {
          console.log(`[vehicleService] VIN ${vehicleData?.vin} already exists — reusing vehicle_id: ${existing?.id}`);
          return existing;
        }
      }

      const snakeData = toSnakeCase({ ...vehicleData, companyId });
      const { data, error } = await supabase
        ?.from('vehicles')
        ?.insert(snakeData)
        ?.select()
        ?.single();

      if (error) {
        console.error('[vehicleService.create] Supabase error:', error);
        throw new Error(`DB error (${error?.code}): ${error?.message}`);
      }

      if (!data) {
        throw new Error('El INSERT no devolvió datos. Verifica políticas RLS en la tabla vehicles.');
      }

      // Fire system event
      try {
        await supabase?.from('system_events')?.insert({
          company_id: companyId,
          event_type: 'vehiculo_creado',
          vehicle_id: data?.id,
          client_id: data?.client_id,
          payload_json: { vin: data?.vin, marca: data?.marca, modelo: data?.modelo }
        });
      } catch {}

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to create vehicle:', error);
      throw error;
    }
  },

  // Insert a pre-built snake_case payload directly — no field mapping applied
  async createRaw(snakePayload) {
    try {
      // If VIN provided and already exists, reuse existing vehicle
      if (snakePayload?.vin) {
        const existing = await this.getByVin(snakePayload?.vin);
        if (existing) {
          console.log(`[vehicleService] VIN ${snakePayload?.vin} already exists — reusing vehicle_id: ${existing?.id}`);
          return existing;
        }
      }

      const { data, error } = await supabase
        ?.from('vehicles')
        ?.insert(snakePayload)
        ?.select()
        ?.single();

      if (error) {
        console.error('[vehicleService.createRaw] Supabase error:', error);
        throw new Error(`DB error (${error?.code}): ${error?.message}`);
      }

      if (!data) {
        throw new Error('El INSERT no devolvió datos. Verifica políticas RLS en la tabla vehicles.');
      }

      // Fire system event (fire-and-forget)
      supabase?.from('system_events')?.insert({
        company_id: snakePayload?.company_id,
        event_type: 'vehiculo_creado',
        vehicle_id: data?.id,
        client_id: data?.client_id,
        payload_json: { vin: data?.vin, marca: data?.marca, modelo: data?.modelo }
      })?.then(() => {})?.catch(() => {});

      return toCamelCase(data);
    } catch (error) {
      console.error('[vehicleService.createRaw] Failed:', error);
      throw error;
    }
  },

  async getByVin(vin) {
    try {
      let companyId = await this.getCompanyId();

      let query = supabase
        ?.from('vehicles')
        ?.select('*, client:clients(id, nombre, telefono, email)')
        ?.eq('vin', vin);

      if (companyId) {
        query = query?.eq('company_id', companyId);
      }

      const { data, error } = await query?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to fetch vehicle by VIN:', error);
      return null;
    }
  },

  async getByClient(clientId) {
    try {
      const { data, error } = await supabase
        ?.from('vehicles')
        ?.select('*')
        ?.eq('client_id', clientId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch vehicles by client:', error);
      return [];
    }
  },

  async getHistory(vehicleId) {
    try {
      const { data, error } = await supabase
        ?.from('work_orders')
        ?.select(`
          id, folio_ot, estado_ot, fecha_ingreso, fecha_entrega, total, notas, created_at,
          client:clients(id, nombre, telefono, email)
        `)
        ?.eq('vehicle_id', vehicleId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch vehicle history:', error);
      return [];
    }
  }
};